import React from 'react';
import { Box, Typography, Container, Grid, Chip, Divider } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TempleBuddhistIcon from '@mui/icons-material/TempleBuddhist';
import CelebrationIcon from '@mui/icons-material/Celebration';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ForestIcon from '@mui/icons-material/Forest';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PlaceIcon from '@mui/icons-material/Place';
import LandscapeIcon from '@mui/icons-material/Landscape';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import { useLanguage } from '../../context/LanguageContext';

const GOLD = '#d4af37';

const historySections = [
  {
    id: 'origin',
    icon: AutoStoriesIcon,
    gradient: ['#8b5cf6', '#7c3aed'] as [string, string],
    title: 'Divine Origin — End of the Dvapara Yuga',
    paragraphs: [
      'According to legend, at the end of the Dvapara Yuga, Lord Parameshwara manifested in a form equal to Mount Kailasa in response to the prayers of sages and ascetics, blessing this sacred site where the temple was later established. In those days this region was dense forest, where the royal sage Muchukunda performed penance.',
      'Lord Parameshwara appeared along with Goddess Parvati and their sons Subrahmanya and Ganesha, as well as the Sun God, and then transformed into five self-manifested sacred stones for worship before becoming unseen.',
      'With the ascension of the sages to heaven, devotion gradually declined and regular worship was interrupted. The sacred stones lay exposed to mist, rain, and sun in the deep forest. The gods themselves are believed to have continued worship. Even today, except during festival seasons, there is no nightly ritual in the temple — it is believed that divine beings perform worship at night.',
    ],
  },
  {
    id: 'historicalSignificance',
    icon: TempleHinduIcon,
    gradient: ['#b45309', '#d97706'] as [string, string],
    title: 'Historical & Mythological Significance',
    paragraphs: [
      'This is a renowned place of worship in Malabar that has proudly earned its place in the pages of history, adorned with centuries of antiquity and the glory of legends. The Muchukunnu region, which came under the authority of the Kodungallur Thampuran through the lineage of the Kurumbranad kings and the Zamorin, was later transferred to the ownership of the Mankootil Tharavad — the local governing family of the region.',
      'During that period, it was the revered elder Sri Chaathukutty Nair, a senior and respected member of the Mankootil family who oversaw the administration of land holdings including both temples, who built the Kottayil Temple as we see it today — along with the surrounding stone walls and the spacious temple pond — elevating this sacred site to its full beauty and grandeur.',
      'The Legend of Muchukunda: The name "Muchukunnu" is tied to the royal sage Muchukunda, who is said to have performed intense penance at this very site. Lord Shiva appeared to him in his divine form, accompanied by his retinue, blessing the land with eternal spiritual energy.',
      'Presiding Deity: Lord Shiva is worshipped here as Vathil Kappavar — The Protector of the Gate. The temple faces east, and the main idol is highly revered for its divine energy and the powerful spiritual vibrations that devotees experience.',
    ],
  },
  {
    id: 'discovery',
    icon: SelfImprovementIcon,
    gradient: ['#f59e0b', '#d97706'] as [string, string],
    title: 'Rediscovery of the Sacred Stones',
    paragraphs: [
      'Long ago, a man collecting firewood sharpened his tool and witnessed divine energy manifest. Frightened and astonished, he ran to inform others. The Namboothiri priest of the Edamana household and his companions came to the forest and beheld the sacred stones.',
      'A woman named Manikyam from the nearby Mangattu house was called, the area was ritually purified, and offerings were made on leaves. This forms the origin legend of the temple. A symbolic ritual commemorating the first discovery still takes place during the annual festival, known as the "Manikye Vili" ceremony.',
    ],
    paragraphsMl: [
      undefined,
      'സമീപത്തുള്ള മംഗാട്ട് വീട്ടിൽ നിന്ന് മാണിക്യം എന്ന സ്ത്രീയെ വിളിക്കുകയും, ആ പ്രദേശം ശുദ്ധീകരിക്കുകയും, ഇലകളിൽ നിവേദ്യം അർപ്പിക്കുകയും ചെയ്തു. ഇതാണ് ക്ഷേത്രത്തിന്റെ ഉത്ഭവ ഐതിഹ്യം. ആദ്യ കണ്ടെത്തലിന്റെ ഓർമ്മയ്ക്കായുള്ള ഒരു പ്രതീകാത്മക ചടങ്ങ് വാർഷിക ഉത്സവ സമയത്ത് ഇപ്പോഴും നടക്കുന്നു, ഇത് "മാണിക്യെ വിളി" ചടങ്ങ് എന്നറിയപ്പെടുന്നു.',
    ],
  },
  {
    id: 'manikyeVili',
    icon: CelebrationIcon,
    gradient: ['#ef4444', '#dc2626'] as [string, string],
    title: 'The Manikye Vili Ceremony',
    titleMl: 'മാണിക്യെ വിളി ചടങ്ങ്',
    paragraphs: [
      'On the festival flag-hoisting day, a humble and devotional procession begins at the Kovilakam temple and proceeds to the Kotta temple. In a deeply spiritual atmosphere, the western door is opened and a ceremonial call is made toward the Mangattu house — "Manikye..." — followed by a resonant response echoing devotion and sacred sound.',
      'This blissful ritual is also performed on the Aarattu day.',
    ],
  },
  {
    id: 'kottiyoor',
    icon: TempleBuddhistIcon,
    gradient: ['#06b6d4', '#0891b2'] as [string, string],
    title: 'Sacred Bond with Kottiyoor & Traditions',
    paragraphs: [
      'The spiritual legacy of the royal sage Muchukunda deeply connects Muchukunnu Kotta to the revered Kottiyoor temple tradition. This sacred bond is reflected in shared ritual practices that echo the grandeur of the ancient Daksha Yaga, linking both temples in an unbroken chain of devotion passed down through generations.',
      'The temple proudly preserves time-honored festival traditions that celebrate this divine heritage. Ritual coconut offerings, the ceremonial placement of tender coconuts, and the sacred coconut bathing ceremony on Aarattu day are vibrant expressions of living faith. Within the temple grounds, a consecrated serpent shrine stands as a testament to the temple\u2019s deep-rooted spiritual traditions, where annual worship, offerings, and serpent rites are reverently performed during the festival season.',
    ],
  },
  {
    id: 'divinepower',
    icon: VolunteerActivismIcon,
    gradient: ['#a855f7', '#7c3aed'] as [string, string],
    title: 'Divine Power & Blessings',
    paragraphs: [
      'It is believed that the temple fulfills the wishes of true devotees. Symbolically linked to Lord Shiva, guardian of Banasura\'s fort, the deity here protects devotees and grants prosperity and well-being. The prominence of Saturn worship is reflected in the large Sunday gatherings, indicating the influence of the Sun God.',
      'Even in earlier times without travel facilities, devotees journeyed from distant places. An elderly devotee from Keezhur, once unable to travel regularly, is believed to have been blessed by the deity appearing near his residence — giving rise to the Keezhur Shiva temple legend, further proclaiming the divine power of the guardian Lord.',
    ],
  },
  {
    id: 'nature',
    icon: ForestIcon,
    gradient: ['#22c55e', '#16a34a'] as [string, string],
    title: 'Sacred Natural Beauty & Temple Atmosphere',
    paragraphs: [
      'The temple is surrounded by breathtaking natural beauty — water-rich landscapes to the north and east, lotus-filled ponds to the west, and land shaped like a Shiva lingam due to water on three sides. Spread over roughly ten acres of dense forest, the temple lies within massive brick walls rising about ten feet like fortress gates. Large stone gateways, sized for elephants, add to its grandeur.',
      'Medicinal plants, creepers, tall trees, birds, flowers, and butterflies enhance the peaceful garden-like environment. Silence within the temple walls is a defining feature. There is no pomp or display; cultural performances and celebrations are not permitted. Even electric lights are absent. Waves of sacred chants flowing from the sanctum are believed to dissolve devotees\' burdens. Devotional singing for inner peace is a long-standing tradition here.',
    ],
  },
  {
    id: 'architectural',
    icon: LandscapeIcon,
    gradient: ['#047857', '#059669'] as [string, string],
    title: 'Architectural & Natural Highlights',
    paragraphs: [
      'Virgin Forest (Kottayil Kavu): The temple is nestled within a ten-acre sacred grove known as Kottayil Kavu. According to the Kerala Forest Research Institute, it is one of only 22 remaining "virgin forests" in Kerala, known for its rich biodiversity and thick vegetation.',
      'Iconic Temple Pond: A major attraction is its large pond with stone steps, which gained fame as the filming location for the 1992 Malayalam movie Sargam. The pond is surrounded by massive trees and often covered with lotus flowers, creating a serene and picturesque setting.',
      'Traditional Kerala Style: The temple features classic Kerala architecture with rare terracotta flower decorations on its walls and a well-structured compound wall, reflecting the artistic mastery of traditional craftsmen.',
    ],
  },
  {
    id: 'kovilakam',
    icon: WaterDropIcon,
    gradient: ['#3b82f6', '#2563eb'] as [string, string],
    title: 'Sri Kovilakam Temple & Community Ties',
    paragraphs: [
      'To the south stands the Sri Kovilakam temple, associated with royal traditions and rituals. The nearby Kadukkuzhi pond enhances the temple\'s beauty and historical character.',
      'The temple also shares ties with the ancient Sri Vazhayil Bhagavathi temple of Muchukunnu. Joint rituals and community participation strengthen social harmony and unity across different communities. Even followers of other faiths respectfully welcome the temple festival. The temple has long been known for its charitable food offerings, whose spiritual merit has been emphasized in temple divinations.',
    ],
  },
  {
    id: 'administration',
    icon: GavelIcon,
    gradient: ['#ef4444', '#dc2626'] as [string, string],
    title: 'Heritage & Modern Administration',
    paragraphs: [
      'This historic temple, enriched by centuries of legend, holds a distinguished place in Malabar\'s heritage. Once under the rule of regional kings and later custodians, the temple\'s present structures — including walls and pond — were built under the leadership of Sri Chathukkutty Nair of the Mangkoottil family.',
      'Temple administration later followed a formal scheme established by the Thalassery Sub Court. After land reform laws reduced income, community participation began supporting temple activities from 1970 onward. In 2003, under the direction of the Kerala High Court, administrative reforms granted broader local participation. Today the temple is managed by a trustee board combining hereditary trustees, elected community representatives, and a court-appointed manager.',
    ],
  },
];

export default function TempleHistory() {
  const { language } = useLanguage();
  const isMl = language === 'ml';
  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Temple History — Kottayil Temple | ക്ഷേത്ര ചരിത്രം" description="Discover the rich history of Kottayil Temple (Muchukunnu Sri Kotta-Kovilakam Kshethram), an ancient temple with centuries of heritage in Kozhikode, Kerala. Famous for Kottayil Kulam." path="/about/history" keywords="Kottayil temple history, Muchukunnu history, Kotta-Kovilakam history, Kottayil kulam history, ancient temple Kerala, കൊട്ടയിൽ ക്ഷേത്രം ചരിത്രം, മുച്ചുകുന്ന് ക്ഷേത്രം, കൊട്ടയിൽ കുളം" />
      <PageBanner title="Temple History" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 40%, #a78bfa 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <HistoryEduIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      A Spiritually Blessed Tapovana Temple
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Sri Muchukunnu Kotta-Kovilakam Kshethram — A Sacred Heritage
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  The renowned Sri Kotta–Kovilakam temples of North Kerala are located at Muchukunnu, along the Muchukunnu–Purakkad road, about 4 km from the Koyilandy–Anakkulam road.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<PlaceIcon sx={{ fontSize: '16px !important', color: '#a78bfa !important' }} />} label="Muchukunnu, Koyilandy" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#4c1d95', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<ForestIcon sx={{ fontSize: '16px !important', color: '#c4b5fd !important' }} />} label="Tapovana Heritage" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* INTRO QUOTE */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 3 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', px: 2.5, py: 1.8, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                    <FormatQuoteIcon sx={{ fontSize: 19, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>Sacred Heritage</Typography>
                </Box>
              </Box>
              <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, position: 'relative' }}>
                <FormatQuoteIcon sx={{ position: 'absolute', top: 12, left: 12, fontSize: 36, color: 'rgba(139,92,246,0.08)', transform: 'scaleX(-1)' }} />
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 2, fontStyle: 'italic', pl: { xs: 0, sm: 2 } }}>
                  The serene forest-like sacred atmosphere, the architectural beauty of the fort and pond, and the temple environment filled with devotion and tranquility — along with simple, spiritually fulfilling rituals — make Sri Kotta Temple unique.
                </Typography>
              </Box>
            </Box>

            {/* HISTORY NARRATIVE SECTIONS */}
            {historySections.map((section) => {
              const SectionIcon = section.icon;
              const [gS, gE] = section.gradient;
              return (
                <Box
                  key={section.id}
                  sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden', mb: 2.5,
                    transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px ${gS}15` },
                  }}
                >
                  <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                        <SectionIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                        {isMl && section.titleMl ? section.titleMl : section.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
                    {section.paragraphs.map((para, pIdx) => {
                      const displayPara = isMl && section.paragraphsMl?.[pIdx] ? section.paragraphsMl[pIdx] : para;
                      return (
                      <React.Fragment key={pIdx}>
                        <Typography sx={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 2, textAlign: 'justify', mb: pIdx < section.paragraphs.length - 1 ? 1.5 : 0 }}>
                          {displayPara}
                        </Typography>
                        {pIdx < section.paragraphs.length - 1 && (
                          <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.04)' }} />
                        )}
                      </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}

            {/* CLOSING DARK CARD */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />

              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <AccountBalanceIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>A Living Heritage</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Centuries of unbroken spiritual tradition</Typography>
                  </Box>
                </Box>

                <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', mb: 2.5 }}>
                  <FormatQuoteIcon sx={{ fontSize: 24, color: `${GOLD}40`, transform: 'scaleX(-1)', mb: -0.5 }} />
                  <Typography sx={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.5)', lineHeight: 2, fontStyle: 'italic' }}>
                    Waves of sacred chants flowing from the sanctum are believed to dissolve devotees' burdens. Devotional singing for inner peace is a long-standing tradition here.
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {[
                    { label: 'Location', value: 'Muchukunnu, North Kerala', icon: PlaceIcon, gradient: ['#8b5cf6', '#7c3aed'] },
                    { label: 'Area', value: '~10 Acres of Sacred Forest', icon: ForestIcon, gradient: ['#22c55e', '#16a34a'] },
                    { label: 'Main Deity', value: 'Lord Parameshwara (Shiva)', icon: SelfImprovementIcon, gradient: ['#f59e0b', '#d97706'] },
                    { label: 'Administration', value: 'Trustee Board & Court Manager', icon: GavelIcon, gradient: ['#3b82f6', '#2563eb'] },
                  ].map((fact, i) => {
                    const FIcon = fact.icon;
                    return (
                      <Grid item xs={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1.3, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: `linear-gradient(135deg, ${fact.gradient[0]}, ${fact.gradient[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 4px 12px ${fact.gradient[0]}25` }}>
                            <FIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.2 }}>{fact.label}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>{fact.value}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about/history" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
